export type ScreenNode = {
  path: string
  label: string
  desc: string
  via?: string
  pending?: boolean
  children?: ScreenNode[]
}

export const PUBLIC_TREE: ScreenNode[] = [
  {
    path: '/',
    label: '홈',
    desc: '최근 등록된 시민문제카드·기업해결카드를 보여줍니다. 헤더 로고가 여기로 옵니다.',
  },
  {
    path: '/about',
    label: '소개',
    desc: '해봄이 무엇을 하는 곳인지, 어떻게 참여하고 등록 뒤에 어떻게 되는지.',
  },
  {
    path: '/problems',
    label: '시민문제카드',
    desc: '시민이 등록한 문제(시민문제카드) 목록입니다. 분야·대상·동으로 거릅니다.',
    children: [
      {
        path: '/problems/new',
        label: '문제 등록',
        desc: '시민이 한 문장으로 불편을 접수합니다. 회원가입 없이 익명 세션으로 묶습니다.',
      },
      {
        path: '/problems/:id',
        label: '문제 상세',
        desc: '접수된 문제 하나의 내용과 상태.',
        via: '시민 문제 목록에서 항목 선택',
      },
    ],
  },
  {
    path: '/solutions',
    label: '기업해결카드',
    desc: '기업이 등록한 해결카드 목록입니다. 매칭 단위는 기업이 아니라 개별 해결카드입니다.',
    children: [
      {
        path: '/solutions/:id',
        label: '해결카드 상세',
        desc: '해결카드 한 건의 도움방식·분야·대상·서비스 지역.',
        via: '기업카드 목록에서 항목 선택',
      },
      {
        path: '/solutions/:id/cases',
        label: '해결 사례',
        desc: '해결카드가 해결한 시민문제 목록입니다. 아직 목이며 — 목록을 주는 라우트가 계약에 없습니다.',
        via: '기업 상세의 해결카드 꼬리줄에서 화살표 선택',
      },
      {
        path: '/orgs/:id',
        label: '기업 상세',
        desc: '기업정보와 그 기업이 가진 해결카드 목록(1기업 : N해결카드).',
        via: '해결카드 상세에서 기업명 선택',
      },
    ],
  },
  {
    path: '/map',
    label: '해봄지도',
    // 실행성과 레이어만 아직 데이터가 없다 — 화면 자체는 열린다. 그 상태는 지도 안에서 밝힌다.
    desc: '수요·해결역량·미충족·실행성과를 동 단위로 봅니다(실행성과는 데이터 준비 중).',
  },
  {
    path: '/board',
    label: '소식·이야기',
    // 한 화면을 `?board=`로 갈라 두 게시판을 본다. 사이트맵은 라우트를 적는 곳이므로
    // 화면 하나로 적고 그 사실을 밝힌다.
    // 내비게이션에서 `소식`·`이야기` 두 링크를 뺐다(사용자 지시 2026-09-07) — 지금 이 화면으로
    // 들어오는 길은 이 사이트맵뿐이다.
    desc: '관리자가 올리는 글. `?board=notice`(소식)·`?board=org_visit`(이야기)로 게시판을 갈라 봅니다.',
    via: '이 사이트맵 (헤더·사이드바에는 링크가 없습니다)',
    children: [
      {
        path: '/board/:id',
        label: '글 상세',
        desc: '글 본문과 첨부 이미지.',
        via: '소식·이야기 목록에서 항목 선택',
      },
    ],
  },
  {
    path: '/privacy',
    label: '개인정보 처리방침',
    desc: '무엇을 왜 받고 언제까지 갖는지. 게시 주체는 주식회사 이티랩(보호법 제30조).',
  },
  {
    path: '/sitemap',
    label: '사이트맵',
    desc: '지금 보고 있는 이 화면.',
  },
  {
    path: '(그 외 모든 주소)',
    label: '찾는 화면이 없습니다',
    desc: '없는 주소로 들어왔을 때 나오는 안내 화면.',
    via: '잘못된 주소 입력 시 자동',
  },
]

export const ADMIN_TREE: ScreenNode[] = [
  {
    path: '/admin/login',
    label: '관리자 로그인',
    desc: '아이디·비밀번호로 관리자 세션을 만듭니다. 로그인 없이도 열리는 유일한 관리자 화면입니다.',
  },
  {
    path: '/admin',
    label: '카드 검수',
    desc: '등록된 카드를 공개할지 판단하고 상태를 전이합니다. 전이 규칙은 서버가 정본입니다.',
  },
  {
    path: '/admin/orgs',
    label: '기업 관리',
    desc: '비공개를 포함한 기업정보 목록입니다. 여기서 기업과 해결카드를 만듭니다.',
    children: [
      { path: '/admin/orgs/new', label: '새 기업', desc: '기업정보를 등록합니다.' },
      {
        path: '/admin/orgs/:id',
        label: '기업 수정',
        desc: '등록된 기업정보를 고칩니다.',
        via: '기업 관리 목록에서 항목 선택',
      },
      {
        path: '/admin/cards/new',
        label: '새 해결카드',
        desc: '기업에 속한 해결카드를 등록합니다. 소속 기업은 주소의 orgId로 받습니다.',
      },
      {
        path: '/admin/cards/:id',
        label: '해결카드 수정',
        desc: '등록된 해결카드를 고칩니다.',
        via: '기업 상세·검수 화면에서 항목 선택',
      },
    ],
  },
  {
    path: '/admin/match',
    label: '매칭',
    desc: '시민문제카드에 해결카드 후보를 붙인다. 제외조건 필터 → 적합도 100점 2단계.',
    children: [
      {
        path: '/admin/match/:id',
        label: '매칭 결과',
        desc: '후보 목록과 점수 근거, 상태 전이.',
        via: '매칭 목록에서 실행 건 선택',
      },
    ],
  },
  {
    path: '/admin/data',
    label: '데이터 관리',
    desc: '카드·기업 데이터를 검색·필터해 CSV/XLSX로 내보냅니다.',
  },
  {
    path: '/admin/posts',
    label: '소식 관리',
    desc: '소식 글을 쓰고 공개·삭제합니다.',
    children: [
      { path: '/admin/posts/new', label: '새 소식', desc: '글을 작성하고 이미지를 첨부합니다.' },
      {
        path: '/admin/posts/:id',
        label: '소식 수정',
        desc: '작성된 글을 고칩니다.',
        via: '소식 관리 목록에서 항목 선택',
      },
    ],
  },
  {
    path: '/admin/dev',
    label: '개발자 패널',
    desc: '구축 담당자용 상태·감사 화면. dev:access 권한 계정으로만 열립니다.',
  },
]
